/**
 * SPA page_view tracking.
 *
 * `gtag("config", GA_ID, { send_page_view: true })` sends exactly one page_view
 * — the initial document load. Every subsequent client-side navigation is
 * invisible to GA4, which made internally-linked pages (the salon pages) report
 * fewer page_views than booking_clicks.
 *
 * This subscribes to the router and sends a page_view per completed navigation,
 * skipping the very first resolution so the initial load is not double counted.
 *
 * The send is deferred one frame + microtask so TanStack's `head()` has written
 * `document.title`; otherwise every page reports the previous page's title.
 */
import type { AnyRouter } from "@tanstack/react-router";

let subscribed = false;

/**
 * Idempotent: React StrictMode double-invokes effects, so the guard prevents a
 * second subscription. No unsubscribe is returned — the router lives as long as
 * the page does, and returning one would let StrictMode's cleanup tear down the
 * only subscription.
 */
export function initPageViewTracking(router: AnyRouter) {
  if (typeof window === "undefined" || subscribed) return;
  subscribed = true;

  // Seeded with the landing URL: `gtag("config", …, { send_page_view: true })`
  // already sent that one, and the URL check below then skips it. Do NOT skip
  // "the first callback" instead — on the initial load onResolved may already
  // have fired before this subscription exists, which would swallow the user's
  // first real navigation.
  let lastSent = window.location.href;

  router.subscribe("onResolved", () => {
    // Wait for the head update (title) to land before reading document.title.
    requestAnimationFrame(() => {
      setTimeout(() => {
        const href = window.location.href;
        if (href === lastSent) return;
        lastSent = href;
        window.gtag?.("event", "page_view", {
          page_location: href,
          page_title: document.title,
        });
        window.dataLayer?.push({
          event: "page_view",
          page_location: href,
          page_title: document.title,
        });
      }, 0);
    });
  });
}
